import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Instagram, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * OrganizerAutocomplete
 * Debounced name-based search against /api/organizers. When the user picks a
 * suggestion, the parent form receives (organizer) so it can autofill the
 * Instagram field. If the user leaves free text (no pick), the parent still
 * gets it via `onTextChange` and can persist it as-is.
 *
 * The visible input is the ORGANIZER NAME. The Instagram is applied via
 * `onSelect(org)` when the user picks a match.
 */
export function OrganizerAutocomplete({
  label = 'Organizador',
  placeholder = 'Escribe el nombre del organizador…',
  initialText = '',
  onSelect,
  onTextChange,
  testId = 'organizer-autocomplete',
}) {
  const [text, setText] = useState(initialText || '');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => { setText(initialText || ''); }, [initialText]);

  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const runSearch = async (term) => {
    if (!term || term.trim().length < 2) { setItems([]); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ q: term.trim(), limit: '10' });
      const res = await axios.get(`${API_URL}/api/organizers?${params.toString()}`);
      setItems(res.data || []);
      setOpen(true);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setText(v);
    onTextChange && onTextChange(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(v), 250);
  };

  const pick = (org) => {
    setText(org.name);
    onTextChange && onTextChange(org.name);
    onSelect && onSelect(org);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      {label && <Label className="flex items-center gap-1"><Instagram className="w-3.5 h-3.5 text-pink-500" /> {label}</Label>}
      <div className="relative">
        <Input
          value={text}
          onChange={handleChange}
          onFocus={() => { if (items.length) setOpen(true); }}
          placeholder={placeholder}
          data-testid={testId}
        />
        {loading && (
          <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
        )}
      </div>
      {open && items.length > 0 && (
        <div
          className="absolute left-0 right-0 z-50 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto"
          data-testid={`${testId}-suggestions`}
        >
          {items.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => pick(o)}
              className="w-full text-left px-3 py-2 hover:bg-pink-50 flex items-start gap-2 border-b last:border-b-0"
              data-testid={`${testId}-item-${o.id}`}
            >
              <Instagram className="w-3.5 h-3.5 mt-1 text-pink-500 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-[#064E3B] truncate">{o.name}</div>
                <div className="text-[11px] text-gray-500 truncate">
                  {o.instagram || 'Sin IG registrado'}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
      {open && !loading && items.length === 0 && text.trim().length >= 2 && (
        <div className="absolute left-0 right-0 z-50 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs text-gray-500">
          Sin coincidencias en el catálogo — se guardará como texto libre.
        </div>
      )}
    </div>
  );
}
